import React from 'react';
import { 
  Flame,
  Recycle,
  Factory,
  LeafyGreen,
  CircleDollarSign,
  Calculator,
  BarChart3,
  ArrowDownToLine,
  TreePine,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../../Images/solidfuel.jpg"
import img1 from "../../Images/convt.jpeg"

const SolidFuelConversionPage = () => {
  const services = [
    {
      title: "Boiler Conversion Solutions",
      description: "Complete system modification and upgrades to enable efficient solid fuel combustion in existing boiler systems.",
      icon: <Flame className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
    },
    {
      title: "Fuel Storage & Handling",
      description: "Design and implementation of automated solid fuel storage and handling systems for seamless operation.",
      icon: <Factory className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
    },
    {
      title: "Environmental Compliance",
      description: "Integration of emission control systems ensuring compliance with environmental regulations.",
      icon: <LeafyGreen className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
    }
  ];

  const benefits = [
    {
      title: "Cost Reduction",
      description: "Significant reduction in fuel costs compared to traditional fossil fuels",
      icon: <CircleDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />,
      stat: "Up to 40%"
    },
    {
      title: "Carbon Footprint",
      description: "Reduced carbon emissions through renewable fuel sources",
      icon: <TreePine className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />,
      stat: "50% Lower"
    },
    {
      title: "ROI Timeline",
      description: "Quick return on investment through operational savings",
      icon: <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />,
      stat: "12-18 Months"
    }
  ];

  const conversionProcess = [
    {
      title: "Assessment",
      description: "Comprehensive evaluation of existing system and requirements"
    },
    {
      title: "Design",
      description: "Custom conversion design and modification planning"
    },
    {
      title: "Implementation",
      description: "System upgrades and new component installation"
    },
    {
      title: "Optimization",
      description: "Performance tuning and efficiency maximization"
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Solid Fuel Conversion Solutions",
    "description": "Industrial boiler conversion to solid fuel including biomass, coal, and renewable fuels with environmental compliance",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Solid Fuel Conversion Services",
      "itemListElement": services.map((service) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Solid Fuel Conversion Solutions | Biomass & Coal Boiler Conversion | Gortherm Energy</title>
        <meta name="description" content="Professional solid fuel conversion services for industrial boilers. Convert to biomass, coal, or renewable solid fuels with automated handling, emission control, and cost-effective operation. Reduce fuel costs up to 40%." />
        <meta name="keywords" content="solid fuel conversion, boiler conversion, biomass conversion, coal fired boiler, fuel conversion, renewable fuels, biomass boiler, solid fuel handling, emission control, fuel cost reduction, boiler retrofit Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Solid Fuel Conversion Solutions | Biomass & Coal Boiler Conversion | Gortherm Energy" />
        <meta property="og:description" content="Transform your industrial heating systems with cost-effective and sustainable solid fuel technologies. Reduce fuel costs up to 40%." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/solid-fuel-conversion" />
        <meta property="og:image" content={img1} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solid Fuel Conversion Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Industrial boiler conversion to solid fuels with automated handling and environmental compliance." />
        <meta name="twitter:image" content={img1} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/solid-fuel-conversion" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="w-full">
        {/* Hero Section */}
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r bg-yellow-900 overflow-hidden pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img
              src={img1}
              alt="Industrial solid fuel conversion and biomass boiler systems by Gortherm Energy"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              width="1920"
              height="400"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
                Solid Fuel Conversion Solutions
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">
                Transform your industrial heating systems with cost-effective and sustainable solid fuel technologies.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href="/contact">
                  <button className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">
                    Get Started
                    <ArrowDownToLine className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Conversion Services</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive solid fuel conversion solutions designed to reduce operational costs 
              while promoting environmental sustainability.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article 
                key={index}
                className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          {/* Benefits Section */}
          <section className="bg-orange-50 rounded-xl p-6 sm:p-8 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Key Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                  <div className="flex justify-center mb-3 sm:mb-4" aria-hidden="true">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{benefit.description}</p>
                  <div className="text-xl sm:text-2xl font-bold text-orange-600">{benefit.stat}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Conversion Process */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Conversion Process</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {conversionProcess.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-orange-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{step.description}</p>
                  </div>
                  {index < conversionProcess.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-orange-100"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Features Grid */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img}
                alt="Solid fuel boiler system with automated handling and combustion control"
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Technology Features</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <Recycle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Automated Fuel Handling</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Efficient conveying systems and storage solutions for continuous operation with minimal manual intervention.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Performance Monitoring</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Real-time efficiency tracking and optimization systems for maximum combustion performance.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <LeafyGreen className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Emission Control</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Advanced pollution control systems ensuring environmental compliance and clean combustion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <aside className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Convert to Solid Fuel?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Consult with our solid fuel conversion experts to reduce costs and improve sustainability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base lg:text-lg transition-all duration-300 shadow-md hover:shadow-xl w-full sm:w-auto">
                  Request Conversion Assessment
                </button>
              </a>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact solid fuel conversion experts at +977 980 278 1400"
              >
                <button className="bg-white hover:bg-gray-50 text-orange-600 border-2 border-orange-600 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold w-full sm:w-auto justify-center">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Call Our Experts</span>
                </button>
              </a>
            </div>
           
          </aside>
        </div>
      </main>
    </>
  );
};

export default SolidFuelConversionPage;